/// @ts-check
/**
 * Tagged Forum 模式的客製化 Context 構建函數
 * 
 * 策略：
 * 1. 在相同 tag 中基於關聯性（節點往上溯源）
 * 2. 若無上層節點或不足時，則在相同 tag 中用內容相關度取用部分內容
 * 
 * 架構原則：
 * 1. 此模組負責 Tagged Forum 模式特有的 context 構建邏輯
 * 2. 使用共用的類型定義（GroupContext, ConversationHistoryItem）
 * 3. 根據 Tagged Forum 的特性客製化處理邏輯
 * 4. 參數明確化：接收標準化的 { nodes, edges } 結構
 */

import { buildUnifiedGroupContext } from '../../../utils/groupContextHelper';
import { traceToRoot } from '../core/CoreForumUtils';
import { getContextBuildParams, getGroupId } from '../../../utils/storageAccessor';
import {
  buildConversationHistoryBatch,
  calculateRelevance,
  RelevancePresets
} from '../../../utils/conversationHistoryHelper';
import {
  extractTimestamp,
  extractTitle
} from '../../../utils/nodeDataExtractor';
import { ConversationHistory, GroupContextValidator } from '../../../types/groupContext.types.js';

/**
 * @typedef {import('../../../types/groupContext.types.js').GroupContext} GroupContext
 * @typedef {import('../../../types/groupContext.types.js').ConversationHistoryItem} ConversationHistoryItem
 * @typedef {import('../../../types/graphData.types.js').GraphData} GraphData
 * @typedef {import('../../../types/graphData.types.js').GraphNode} GraphNode
 * @typedef {import('../../../types/graphData.types.js').GraphEdge} GraphEdge
 */

/**
 * 構建 Tagged Forum 模式的小組 context（包含對話歷史）
 * 
 * Tagged Forum 特性：
 * - 以標籤為中心的結構化討論
 * - 支援多種標籤類型（問題、回覆、想法等）
 * - 階層式的討論結構
 * 
 * @param {Array<any>} graphNodes - 圖形節點（需包含 _edges 臨時字段）
 * @param {string} currentContent - 當前內容
 * @param {string} [currentTag=''] - 當前標籤類型
 * @param {number} [limit=10] - 對話歷史限制數量
 * @returns {GroupContext} Tagged Forum 專用的完整小組 context
 */
export const buildTaggedForumContext = (graphNodes = [], currentContent, currentTag = '', limit = 10) => {
  console.log('[buildTaggedForumContext] 輸入參數:', {
    nodeCount: graphNodes?.length || 0,
    contentLength: currentContent?.length || 0,
    tag: currentTag,
    limit
  });
  
  // 從頁面層級獲取參數
  const params = getContextBuildParams();
  
  // 使用基礎構建函數
  const mode = currentTag === 'chat' ? 'chat' : 'sticker';
  const baseContext = buildUnifiedGroupContext(graphNodes, params, mode);
  
  // ✅ 從臨時字段提取 edges（與 chatRoomContext 一致）
  const edges = graphNodes[0]?._edges || [];
  
  console.log('[buildTaggedForumContext] 輸入:', {
    nodeCount: graphNodes.length,
    edgeCount: edges.length,
    contentLength: currentContent?.length || 0,
    tag: currentTag,
    limit
  });
  
  // ✅ 使用 ConversationHistory 類構建對話歷史
  const conversationHistory = getTaggedForumConversationHistory(
    graphNodes, 
    edges, 
    currentContent,
    currentTag,
    limit
  );
  
  console.log('[buildTaggedForumContext] 輸出:', {
    historyCount: conversationHistory.getCount(),
    participants: [...new Set(conversationHistory.toArray().map(h => h.author))]
  });
  
  // ✅ 使用 GroupContextValidator 創建 context，確保類型正確
  return GroupContextValidator.createWithHistory(
    {
      ...baseContext,
      interactionType: currentTag === 'chat' ? 'real-time-chat' : 'idea-creation',
      expectation: `標籤式討論 - 當前標籤: ${currentTag || '無'}`,
      contextVersion: '3.1-tagged-forum-standardized'
    },
    conversationHistory
  );
};

/**
 * 獲取 Tagged Forum 模式的對話歷史
 * ✅ 使用 ConversationHistory 類，提供類型安全和便捷操作
 * 
 * Tagged Forum 策略：
 * 1. 在相同 tag 中基於關聯性（節點往上溯源）
 * 2. 若無上層節點或不足時，則在相同 tag 中用內容相關度補充
 * 
 * @param {Array<any>} nodes - 所有節點
 * @param {Array<any>} edges - 所有邊
 * @param {string} currentContent - 當前內容
 * @param {string} currentTag - 當前標籤
 * @param {number} [limit=10] - 對話歷史限制數量
 * @returns {ConversationHistory} ConversationHistory 實例
 */
const getTaggedForumConversationHistory = (nodes, edges, currentContent, currentTag, limit = 10) => {
  if (!nodes || nodes.length === 0) {
    return ConversationHistory.empty();
  }

  // 第一步：過濾同 tag 的節點
  const filteredNodes = currentTag 
    ? filterNodesByTag(nodes, currentTag)
    : nodes;

  console.log('[TaggedForum] Tag 過濾:', {
    原始節點數: nodes.length,
    過濾後節點數: filteredNodes.length,
    tag: currentTag
  });

  if (filteredNodes.length === 0) {
    console.warn('[TaggedForum] 沒有找到符合 tag 的節點');
    return ConversationHistory.empty();
  }

  // ✅ 優先使用父節點 (_currentNodeId) 作為追溯起點
  const currentNodeId = nodes[0]?._currentNodeId;
  
  let traceFromNodeId;
  if (currentNodeId) {
    // 回覆模式：從父節點追溯
    traceFromNodeId = String(currentNodeId);
    console.log('[TaggedForum] 從父節點追溯:', traceFromNodeId);
  } else {
    // 新 START 節點：使用最新節點
    const sortedNodes = [...filteredNodes].sort((a, b) => {
      const timeA = extractTimestamp(a);
      const timeB = extractTimestamp(b);
      return timeB - timeA; // 降序
    });
    traceFromNodeId = String(sortedNodes[0].id);
    console.log('[TaggedForum] 新 START 節點，使用最新節點:', traceFromNodeId);
  }

  // ✅ 第二步：基於關聯性（往上溯源）- 使用 traceToRoot
  const tracedPath = traceToRoot(traceFromNodeId, edges, filteredNodes);
  console.log('[TaggedForum] 溯源結果:', {
    起點: traceFromNodeId,
    ancestorCount: tracedPath.length,
    targetCount: limit
  });

  // traceToRoot 返回的是 node.data.source，需要轉換為完整節點
  let selectedNodes = tracedPath.map(source => {
    return filteredNodes.find(n => String(n.id) === String(source.id)) || 
           { id: source.id, data: { source } };
  });

  // 第三步：如果溯源節點不足，用內容相關度補充（限定在同 tag 中）
  if (selectedNodes.length < limit && currentContent) {
    const remainingCount = limit - selectedNodes.length;
    const selectedIds = new Set(selectedNodes.map(n => String(n.id)));
    
    // 準備已排序的節點用於補充
    const sortedNodes = [...filteredNodes].sort((a, b) => {
      const timeA = extractTimestamp(a);
      const timeB = extractTimestamp(b);
      return timeB - timeA; // 降序
    });
    
    // 計算相關度（只在同 tag 的節點中，使用 Tagged Forum 預設配置）
    const nodesWithRelevance = sortedNodes
      .filter(node => !selectedIds.has(String(node.id)))
      .map(node => ({
        node,
        relevance: calculateRelevance(node, currentContent, RelevancePresets.taggedForum)
      }))
      .sort((a, b) => b.relevance - a.relevance)
      .slice(0, remainingCount);
    
    const additionalNodes = nodesWithRelevance.map(item => item.node);
    selectedNodes = [...selectedNodes, ...additionalNodes];
    
    console.log('[TaggedForum] 相關度補充:', {
      補充數量: additionalNodes.length,
      最終總數: selectedNodes.length,
      平均相關度: nodesWithRelevance.length > 0
        ? (nodesWithRelevance.reduce((sum, item) => sum + item.relevance, 0) / nodesWithRelevance.length).toFixed(2)
        : 0
    });
  }

  // ✅ 使用模組化批量構建方法（自動驗證，不使用完整文本）
  const rawHistory = buildConversationHistoryBatch(selectedNodes, {
    useFullText: false,
    validate: true // 啟用類型驗證
  });

  // ✅ 使用 ConversationHistory 類包裝，提供鏈式操作
  const history = ConversationHistory
    .fromArray(rawHistory)
    .deduplicate();           // 去重

  console.log('[TaggedForum] 最終歷史:', {
    選中節點數: selectedNodes.length,
    最終歷史數: history.getCount(),
    參與者: history.getCount() > 0 ? [...new Set(history.toArray().map(h => h.author))] : []
  });

  return history;
};

/**
 * 根據標籤類型過濾節點
 * @param {Array<any>} nodes - 所有節點
 * @param {string} tagType - 標籤類型
 * @returns {Array<any>} 過濾後的節點
 */
const filterNodesByTag = (nodes, tagType) => {
  if (!tagType) return nodes;
  
  return nodes.filter(node => {
    const nodeGroup = node.data?.source?.group || node.group;
    
    // 嘗試解析 group 字段
    if (typeof nodeGroup === 'string') {
      try {
        const groupObj = JSON.parse(nodeGroup);
        return groupObj.type === tagType || groupObj.tag === tagType;
      } catch {
        return nodeGroup === tagType;
      }
    } else if (typeof nodeGroup === 'object' && nodeGroup !== null) {
      return nodeGroup.type === tagType || nodeGroup.tag === tagType;
    }
    
    return false;
  });
};

